/**
 * The pure parts of Look on the Review tab's client versions (ModelLook.jsx;
 * the server side is functions/src/zelma/services/versionLook.ts): which
 * files the Suite's viewer can show, which one it opens first, how a mesh's
 * own files (MTL, textures, a glTF's buffers) are found among the version's
 * signed links, and the refusals in plain words. No three.js here.
 */

/** The largest file the viewer downloads into the browser (bigger ones are listed, to open on a computer). */
export const VIEW_MAX_BYTES = 512 * 1024 ** 2;
/**
 * The largest file of each kind the viewer opens: what a kind's loader builds
 * grows with the file (an OBJ's text, a splat's expanded points), so the
 * text and compressed kinds open smaller files.
 */
export const VIEW_MAX_BYTES_BY_KIND = { gltf: 512 * 1024 ** 2, ply: 256 * 1024 ** 2, obj: 64 * 1024 ** 2, splat: 256 * 1024 ** 2 };
/** An ASCII PLY is parsed as text: a smaller one only (plyProblem). */
const PLY_ASCII_MAX_BYTES = 64 * 1024 ** 2;
const SPLAT_COMPRESSED_MAX_BYTES = 64 * 1024 ** 2;
/** A glTF's JSON (a .gltf file, a GLB's JSON chunk) is parsed whole before anything is checked: a small one only. */
export const GLTF_JSON_MAX_BYTES = 32 * 1024 ** 2;
/** The most a file of this name may be, here. */
export const viewLimitOf = (name) => {
  const kind = viewKindOf(name);
  if (!kind) return 0;
  if (/\.gltf$/i.test(String(name))) return GLTF_JSON_MAX_BYTES;
  return /\.(spz|sog)$/i.test(String(name)) ? SPLAT_COMPRESSED_MAX_BYTES : VIEW_MAX_BYTES_BY_KIND[kind];
};
/** Links are asked again this long before they end (a download started before the end goes on). */
export const LINK_MARGIN_MS = 60_000;

const ext = (name) => {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(name ?? ""));
  return m ? m[1].toLowerCase() : "";
};

/** How the viewer shows a file: "gltf", "obj", "ply" (a mesh, points or a splat: decided by its header), "splat", or null. */
export const viewKindOf = (name) => {
  const e = ext(name);
  if (e === "glb" || e === "gltf") return "gltf";
  if (e === "obj") return "obj";
  if (e === "ply") return "ply";
  // SOG (a bundle of images) is not opened here: its images' sizes are not known before Spark decodes them.
  // KSPLAT (a packed format whose count is not read here) is not opened either.
  // Splats: SPZ only (checked from its header); a .splat or other raw format is not opened here (Spark reads
  // by content, so its count could not be checked first).
  if (e === "spz") return "splat";
  return null;
};

/** Why a file is not shown here, in words (null when it is). */
export const notShownWhy = (file) => {
  const e = ext(file?.name);
  if (!file?.url) return "No copy to open yet.";
  if (viewKindOf(file.name)) {
    if ((file.bytes ?? 0) > viewLimitOf(file.name)) return "Too large to open in the browser: open the file on a computer.";
    return null;
  }
  if (e === "usdz") return "USDZ opens on an iPhone, iPad or Mac, not here.";
  if (e === "mtl" || e === "bin" || e === "jpg" || e === "jpeg" || e === "png" || e === "webp" || e === "ktx2") return "Part of the model (opened with it).";
  if (e === "zip") return "An archive: open the file on a computer.";
  if (e === "json" || e === "txt" || e === "csv") return "Not a model.";
  return "This kind of file can't be shown here.";
};

const ORDER = { gltf: 0, obj: 1, ply: 2, splat: 3 };
/** The files the viewer can open, best first (a mesh before a point cloud or splat; then the smaller). */
export const viewableFiles = (files) => (files ?? [])
  .filter((f) => notShownWhy(f) === null)
  .sort((a, b) => ORDER[viewKindOf(a.name)] - ORDER[viewKindOf(b.name)] || (a.bytes ?? 0) - (b.bytes ?? 0) || a.name.localeCompare(b.name));

/**
 * A file a model names (an OBJ's MTL, a texture, a glTF buffer), found among
 * the version's own signed links by its name, or null: nothing outside the
 * version is ever fetched (no other host, no path off the version).
 */
export const companionUrl = (files, ref) => {
  let name = String(ref ?? "");
  try { name = decodeURIComponent(name); } catch { /* as written */ }
  name = name.split(/[?#]/)[0].split(/[\\/]/).pop() ?? "";
  if (!name) return null;
  const hit = (files ?? []).find((f) => f.url && f.name === name) ?? (files ?? []).find((f) => f.url && f.name.toLowerCase() === name.toLowerCase());
  return hit ? hit.url : null;
};

/** Whether a PLY's header (its first bytes) is a Gaussian splat's (spherical-harmonic colours), not a mesh's or a point cloud's. */
export const isSplatPly = (bytes) => {
  const head = new TextDecoder("latin1").decode(bytes.subarray(0, Math.min(bytes.length, 64 * 1024)));
  const end = head.indexOf("end_header");
  const header = end >= 0 ? head.slice(0, end) : head;
  return /property\s+\w+\s+f_dc_0\b/.test(header) && /property\s+\w+\s+(opacity|scale_0)\b/.test(header);
};

/** Whether a look's links still have time left (asked again otherwise). */
export const linksFresh = (look, now = Date.now()) => Boolean(look?.expiresAt) && now < look.expiresAt - LINK_MARGIN_MS;

/** A preview is approved only once every one of its files could be opened (as a guest model's thumbnail is seen first). */
export const lookedAtWhole = (look) => Boolean(look?.files?.length) && look.files.every((f) => f.url);

export const sizeOf = (bytes) => (bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB`
  : bytes >= 1024 ** 2 ? `${(bytes / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round((bytes ?? 0) / 1024))} KB`);

/** The look callable's refusal in plain words. */
export function lookErrorText(e) {
  const code = String(e?.details?.reason || e?.message || "");
  if (code === "not_found") return "Not found for this job.";
  if (code === "not_reviewable") return "Raw and project files are not looked at here.";
  if (code === "not_reported") return "This version is not finished yet.";
  if (/deletion/.test(code)) return "A deletion was asked for it: its files are not opened.";
  if (code === "asset_not_live" || code === "asset_missing") return "This capture is no longer live.";
  if (code === "no_archive_binding" || code === "no_preview_binding" || code === "no_org") return "This client's storage is not set up, so there is nothing to open.";
  if (/Requires role/.test(code) || code === "permission-denied") return "Only an admin can look at client versions.";
  return `Could not open its files (${code || "no answer"}). Try again.`;
}

/* ── budgets: what one look may download and build in the browser ── */

/** All of one look's downloads together (the model and every file it names). */
export const LOOK_TOTAL_BYTES = 1.5 * 1024 ** 3;
/** What a glTF's accessors and compressed views may decode to, together. */
export const GLTF_DECODED_MAX_BYTES = 1024 ** 3;
/** All of one look's images together, decoded (pixels; four bytes each). */
export const LOOK_MAX_PIXELS = 256 * 1024 * 1024;
/** The most images one model may name, and the largest side and pixel count of one. */
export const MAX_IMAGES = 64;
export const MAX_IMAGE_SIDE = 16384;
export const MAX_IMAGE_PIXELS = 128 * 1024 * 1024;

const COMPONENT_BYTES = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 };
const TYPE_COMPONENTS = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT2: 4, MAT3: 9, MAT4: 16 };

/**
 * A glTF's JSON: the .gltf text, or a GLB's JSON chunk and its binary chunk,
 * or null. A GLB is read strictly, so the loader reads exactly what was
 * checked: its declared length is the file's, one JSON chunk first, at most
 * one BIN chunk after, nothing else.
 */
export const gltfParts = (bytes, name) => {
  try {
    if (/\.gltf$/i.test(String(name))) {
      return bytes.byteLength > GLTF_JSON_MAX_BYTES ? null : { json: JSON.parse(new TextDecoder("utf-8").decode(bytes)), bin: null };
    }
    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.byteLength < 20 || dv.getUint32(0, true) !== 0x46546c67 || dv.getUint32(4, true) !== 2) return null;
    if (dv.getUint32(8, true) !== bytes.byteLength) return null;
    const jsonLen = dv.getUint32(12, true);
    if (dv.getUint32(16, true) !== 0x4e4f534a || 20 + jsonLen > bytes.byteLength || jsonLen > GLTF_JSON_MAX_BYTES) return null;
    const json = JSON.parse(new TextDecoder("utf-8").decode(bytes.subarray(20, 20 + jsonLen)));
    let bin = null;
    let at = 20 + jsonLen;
    if (at + 8 <= bytes.byteLength) {
      const len = dv.getUint32(at, true);
      if (dv.getUint32(at + 4, true) !== 0x004e4942 || at + 8 + len > bytes.byteLength) return null;
      bin = bytes.subarray(at + 8, at + 8 + len);
      at += 8 + len;
    }
    if (at !== bytes.byteLength) return null;
    return { json, bin };
  } catch {
    return null;
  }
};

/** Why a glTF would build more than a look may (its decoded size, counts, images), or null. */
/** The glTF extensions a look builds; any other (instancing, Draco, Basis, WebP, …) is opened on a computer. */
const GLTF_EXTENSIONS = new Set([
  // Only ones three's GLTFLoader handles itself (an unknown one is copied into every clone's userData).
  "KHR_materials_unlit", "KHR_materials_emissive_strength", "KHR_materials_specular",
  "KHR_materials_ior", "KHR_mesh_quantization", "EXT_meshopt_compression", "KHR_meshopt_compression",
]);
const GLTF_MAX_NODES = 100_000;
const GLTF_MAX_DRAWN_VERTICES = 60_000_000;
const GLTF_MAX_PRIMITIVES = 100_000;
/** Every extension key a glTF uses, wherever it is (declared or not: the loader acts on any it knows). */
const extensionKeys = (json) => {
  const keys = new Set();
  const walk = (o, depth) => {
    if (!o || typeof o !== "object" || depth > 64) return;
    if (Array.isArray(o)) { for (const x of o) walk(x, depth + 1); return; }
    for (const [k, v] of Object.entries(o)) {
      // `extensions` is a plain object of named extensions, or the file is not read (an array's items would be metadata).
      if (k === "extensions" && (!v || typeof v !== "object" || Array.isArray(v))) keys.add("(not an object)");
      if (k === "extensions" && v && typeof v === "object" && !Array.isArray(v)) for (const e of Object.keys(v)) keys.add(e);
      if (k !== "extras") walk(v, depth + 1);
    }
  };
  walk(json, 0);
  for (const e of [...(Array.isArray(json.extensionsUsed) ? json.extensionsUsed : []), ...(Array.isArray(json.extensionsRequired) ? json.extensionsRequired : [])]) keys.add(e);
  return keys;
};
/** The mesh primitives the loader builds: each scene's node tree walked, a mesh counted once per node that holds it. */
const builtPrimitives = (json) => {
  const nodes = Array.isArray(json.nodes) ? json.nodes : [];
  const meshes = Array.isArray(json.meshes) ? json.meshes : [];
  let prims = 0;
  let visits = 0;
  let tooDeep = false;
  let shared = false;
  let drawn = 0;
  // A strict forest: each node in the tree once (a node reached twice, as a second root or a shared child,
  // would be cloned by the loader with everything it carries).
  const placed = new Set();
  const walk = (i, depth, seen) => {
    if (!Number.isInteger(i) || i < 0 || i >= nodes.length) return;
    // A cycle (a node its own descendant) or a node reached twice: refused (the loader would recurse or clone).
    if (seen.has(i) || placed.has(i)) { shared = true; return; }
    placed.add(i);
    // Deeper than this is refused, never skipped (the loader would build what a skipped walk did not count).
    if (depth > 256) { tooDeep = true; return; }
    visits++;
    if (visits > GLTF_MAX_NODES || prims > GLTF_MAX_PRIMITIVES) return;
    const n = nodes[i];
    const m = Number.isInteger(n?.mesh) ? meshes[n.mesh] : null;
    prims += Array.isArray(m?.primitives) ? m.primitives.length : 0;
    // What each drawn instance submits: its index count, or its position count when it has no indices.
    for (const p of Array.isArray(m?.primitives) ? m.primitives : []) {
      const acc = Array.isArray(json.accessors) ? json.accessors : [];
      const a = Number.isInteger(p?.indices) ? acc[p.indices] : acc[p?.attributes?.POSITION];
      drawn += typeof a?.count === "number" ? a.count : 0;
    }
    const next = new Set(seen).add(i);
    for (const c of Array.isArray(n?.children) ? n.children : []) walk(c, depth + 1, next);
  };
  const scenes = Array.isArray(json.scenes) && json.scenes.length ? json.scenes : [{ nodes: nodes.map((_, i) => i) }];
  for (const sc of scenes) for (const r of Array.isArray(sc?.nodes) ? sc.nodes : []) walk(r, 0, new Set());
  return { prims, visits, tooDeep, shared, drawn };
};
/** The longest data: URI a look decodes (larger inline buffers or images are opened on a computer). */
const DATA_URI_MAX_CHARS = 4 * 1024 * 1024;
/** The glTF properties that name another object by its index: each must be a plain integer, in range. */
const INDEX_KEYS = new Set(["mesh", "skin", "camera", "scene", "indices", "material", "bufferView", "buffer", "source", "sampler", "index", "input", "output", "node"]);
const INDEX_LISTS = new Set(["children", "nodes", "joints"]);
const RANGE_OF = { mesh: "meshes", camera: "cameras", scene: "scenes", indices: "accessors", material: "materials", bufferView: "bufferViews", buffer: "buffers", source: "images", sampler: "samplers", children: "nodes", nodes: "nodes" };
const indexProblem = (json) => {
  const len = (k) => (Array.isArray(json[k]) ? json[k].length : 0);
  const ok = (range, v) => typeof v === "number" && Number.isSafeInteger(v) && v >= 0 && (!range || v < len(range));
  let bad = false;
  // Inside one object of a top-level list: every index property, at any depth (extras aside).
  const walk = (o, depth) => {
    if (bad || !o || typeof o !== "object" || depth > 64) return;
    if (Array.isArray(o)) { for (const x of o) walk(x, depth + 1); return; }
    for (const [k, v] of Object.entries(o)) {
      if (k === "extras") continue;
      if (k === "attributes" && v && typeof v === "object" && !Array.isArray(v)) {
        if (Object.values(v).some((x) => !ok("accessors", x))) bad = true;
        continue;
      }
      if (INDEX_KEYS.has(k)) {
        // A texture reference's `index` is a texture's.
        if (!ok(k === "index" ? "textures" : RANGE_OF[k], v)) bad = true;
        continue;
      }
      if (INDEX_LISTS.has(k)) {
        if (!Array.isArray(v) || v.some((x) => !ok(RANGE_OF[k], x))) bad = true;
        continue;
      }
      walk(v, depth + 1);
    }
  };
  if (json.scene !== undefined && !ok("scenes", json.scene)) return "unreadable";
  for (const [k, v] of Object.entries(json)) {
    if (k === "extras" || k === "asset" || k === "extensionsUsed" || k === "extensionsRequired") continue;
    if (Array.isArray(v)) for (const item of v) walk(item, 1);
  }
  return bad ? "unreadable" : null;
};
/** Where a glTF must hold an array (when present): the loader accepts array-likes the counts would not see. */
const TOP_ARRAYS = ["accessors", "animations", "buffers", "bufferViews", "cameras", "images", "materials", "meshes", "nodes", "samplers",
  "scenes", "skins", "textures", "extensionsUsed", "extensionsRequired"];
const shapeProblem = (json) => {
  if (Array.isArray(json)) return "unreadable";
  for (const k of TOP_ARRAYS) if (json[k] !== undefined && !Array.isArray(json[k])) return "unreadable";
  for (const k of TOP_ARRAYS) if (Array.isArray(json[k]) && json[k].some((x) => typeof x !== "object" && !(k.startsWith("extensions") && typeof x === "string"))) return "unreadable";
  for (const m of Array.isArray(json.meshes) ? json.meshes : []) {
    if (!m || !Array.isArray(m.primitives)) return "unreadable";
    // The fields a mesh has in glTF, and no others (the loader reads some of its own, such as isSkinnedMesh).
    if (Object.keys(m).some((k) => !["primitives", "name", "extensions", "extras"].includes(k))) return "unsupported_model";
    for (const p of m.primitives) {
      if (!p || typeof p !== "object" || Array.isArray(p)) return "unreadable";
      if (Object.keys(p).some((k) => !["attributes", "indices", "material", "mode", "targets", "extensions", "extras"].includes(k))) return "unsupported_model";
      if (p.targets !== undefined && !Array.isArray(p.targets)) return "unreadable";
      if (p.attributes !== undefined && (typeof p.attributes !== "object" || Array.isArray(p.attributes))) return "unreadable";
      // glTF's own attribute names only (the loader lowercases others, so `position` would draw as POSITION).
      if (p.attributes && Object.keys(p.attributes).some((k) => !/^(POSITION|NORMAL|TANGENT|(TEXCOORD|COLOR)_\d)$/.test(k))) return "unsupported_model";
    }
  }
  for (const n of Array.isArray(json.nodes) ? json.nodes : []) {
    if (n && n.children !== undefined && !Array.isArray(n.children)) return "unreadable";
    // glTF's node fields only (the loader also reads marks of its own, such as isBone).
    if (n && Object.keys(n).some((k) => !["name", "children", "mesh", "matrix", "rotation", "scale", "translation", "camera", "skin", "weights", "extensions", "extras"].includes(k))) {
      return "unsupported_model";
    }
  }
  for (const sc of Array.isArray(json.scenes) ? json.scenes : []) if (sc && sc.nodes !== undefined && !Array.isArray(sc.nodes)) return "unreadable";
  return null;
};
export const gltfBudgetProblem = (json) => {
  if (!json || typeof json !== "object") return "unreadable";
  const shape = shapeProblem(json);
  if (shape) return shape;
  const used = [...extensionKeys(json)];
  if (used.some((e) => !GLTF_EXTENSIONS.has(e))) return used.some((e) => /draco/i.test(String(e))) ? "draco" : "unsupported_model";
  // Every extension used must be declared (the loader treats an undeclared one as unknown metadata).
  const declared = new Set(Array.isArray(json.extensionsUsed) ? json.extensionsUsed : []);
  if (used.some((e) => !declared.has(e))) return "unsupported_model";
  const idx = indexProblem(json);
  if (idx) return idx;
  // A texture read through another UV set is a separate copy (texture transforms are refused above).
  if (JSON.stringify(json.materials ?? []).match(/"texCoord":(?!0[,}])/)) return "unsupported_model";
  if ((Array.isArray(json.nodes) ? json.nodes.length : 0) > GLTF_MAX_NODES) return "too_large";
  // Animations, morph targets and skins are not built here (the loader allocates per channel, per target,
  // per joint and per skeleton); nor more than one scene (the loader builds every scene, cloning the nodes
  // and their extras that scenes share).
  if ((Array.isArray(json.skins) && json.skins.length) || (Array.isArray(json.scenes) && json.scenes.length > 1) ||
      (Array.isArray(json.nodes) ? json.nodes : []).some((n) => n?.skin !== undefined) ||
      (Array.isArray(json.animations) && json.animations.length) ||
      (Array.isArray(json.meshes) ? json.meshes : []).some((m) => (Array.isArray(m?.primitives) ? m.primitives : []).some((p) => Array.isArray(p?.targets) && p.targets.length))) {
    return "unsupported_model";
  }
  // Triangles (the default) and points only: strips and fans are copied per primitive by the loader.
  if ((Array.isArray(json.meshes) ? json.meshes : []).some((m) => (Array.isArray(m?.primitives) ? m.primitives : [])
    .some((p) => p?.mode !== undefined && p.mode !== 4 && p.mode !== 0))) return "unsupported_model";
  const built = builtPrimitives(json);
  if (built.shared) return "unsupported_model";
  // The vertices drawn each frame, every instance of a reused mesh counted.
  if (built.drawn > GLTF_MAX_DRAWN_VERTICES) return "too_large";
  if (built.tooDeep || built.prims > GLTF_MAX_PRIMITIVES || built.visits > GLTF_MAX_NODES) return "too_large";
  // Nodes outside every scene are built too (an animation's or a skin's): every node holding a mesh counts
  // once more, on top of the scenes' count.
  const nodes = Array.isArray(json.nodes) ? json.nodes : [];
  const meshes = Array.isArray(json.meshes) ? json.meshes : [];
  const everyNode = nodes.reduce((n, nd) => n + (Number.isInteger(nd?.mesh) && Array.isArray(meshes[nd.mesh]?.primitives) ? meshes[nd.mesh].primitives.length : 0), 0);
  if (built.prims + everyNode > GLTF_MAX_PRIMITIVES) return "too_large";
  const images = Array.isArray(json.images) ? json.images : [];
  // An image names one source: its bytes are checked from it, and the loader reads the same one.
  if (images.some((im) => typeof im?.uri === "string" && im?.bufferView !== undefined)) return "unreadable";
  // An image in a compressed buffer view would be checked against other bytes than the loader decodes.
  const views = Array.isArray(json.bufferViews) ? json.bufferViews : [];
  if (images.some((im) => Number.isInteger(im?.bufferView) && views[im.bufferView]?.extensions)) return "unsupported_model";
  // Inline data: URIs only small ones (decoded before any check otherwise).
  if ([...(Array.isArray(json.buffers) ? json.buffers : []), ...images].some((x) => typeof x?.uri === "string" && x.uri.startsWith("data:") && x.uri.length > DATA_URI_MAX_CHARS)) return "too_large";
  let total = 0;
  // Every buffer view is copied once by the loader (its byteLength), whatever reads it. Lengths and
  // offsets are plain numbers within their buffer (a string would be concatenated by the loader).
  const int = (x) => typeof x === "number" && Number.isSafeInteger(x) && x >= 0;
  const buffers = Array.isArray(json.buffers) ? json.buffers : [];
  if (buffers.some((b) => !int(b?.byteLength))) return "unreadable";
  for (const v of Array.isArray(json.bufferViews) ? json.bufferViews : []) {
    const off = v?.byteOffset === undefined ? 0 : v.byteOffset;
    if (!int(v?.byteLength) || !int(off) || !Number.isInteger(v?.buffer) || !buffers[v.buffer]) return "unreadable";
    if (off + v.byteLength > buffers[v.buffer].byteLength) return "unreadable";
    if (v.byteStride !== undefined && !int(v.byteStride)) return "unreadable";
    total += v.byteLength;
    if (total > GLTF_DECODED_MAX_BYTES) return "too_large";
  }
  for (const a of Array.isArray(json.accessors) ? json.accessors : []) {
    if (typeof a?.count !== "number" || (a.byteOffset !== undefined && !int(a.byteOffset))) return "unreadable";
    // A sparse accessor is copied whole (count × stride) by the loader: not built here.
    if (a?.sparse !== undefined) return "unsupported_model";
    const count = Number(a?.count);
    // Only glTF's own component types and element types (own properties: never an inherited name).
    if (!Object.hasOwn(COMPONENT_BYTES, a?.componentType) || typeof a?.type !== "string" || !Object.hasOwn(TYPE_COMPONENTS, a.type)) return "unreadable";
    const per = COMPONENT_BYTES[a.componentType] * TYPE_COMPONENTS[a.type];
    if (!Number.isSafeInteger(count) || count < 0) return "unreadable";
    // An interleaved accessor (its view has a stride) is uploaded as its own count × stride buffer.
    const view = Number.isInteger(a?.bufferView) && Array.isArray(json.bufferViews) ? json.bufferViews[a.bufferView] : null;
    const stride = view && typeof view.byteStride === "number" ? Math.max(view.byteStride, per) : per;
    total += count * stride;
    if (!Number.isFinite(total)) return "unreadable";
    if (total > GLTF_DECODED_MAX_BYTES) return "too_large";
  }
  for (const v of Array.isArray(json.bufferViews) ? json.bufferViews : []) {
    for (const ext of [v?.extensions?.EXT_meshopt_compression, v?.extensions?.KHR_meshopt_compression]) {
      if (!ext) continue;
      // What the decoder allocates: count × byteStride (byteLength is the compressed input).
      const count = ext.count;
      const stride = ext.byteStride;
      if (!int(count) || !int(stride) || !int(ext.byteLength) || (ext.byteOffset !== undefined && !int(ext.byteOffset))) return "unreadable";
      // The decoder's own rules, before it is called (a bad mode or filter would fail after it allocates).
      if (!["ATTRIBUTES", "TRIANGLES", "INDICES"].includes(ext.mode)) return "unreadable";
      if (ext.filter !== undefined && !["NONE", "OCTAHEDRAL", "QUATERNION", "EXPONENTIAL"].includes(ext.filter)) return "unreadable";
      if (ext.mode === "ATTRIBUTES" && (stride % 4 !== 0 || stride === 0 || stride > 256)) return "unreadable";
      if (ext.mode !== "ATTRIBUTES" && stride !== 2 && stride !== 4) return "unreadable";
      if (ext.mode === "TRIANGLES" && count % 3 !== 0) return "unreadable";
      // Filters as the extension defines them: none for index data; octahedral 4 or 8, quaternion 8,
      // exponential a multiple of 4.
      const filter = ext.filter ?? "NONE";
      if (ext.mode !== "ATTRIBUTES" && filter !== "NONE") return "unreadable";
      if (filter === "OCTAHEDRAL" && stride !== 4 && stride !== 8) return "unreadable";
      if (filter === "QUATERNION" && stride !== 8) return "unreadable";
      if (filter === "EXPONENTIAL" && stride % 4 !== 0) return "unreadable";
      if (typeof v?.byteLength === "number" && v.byteLength !== count * stride) return "unreadable";
      total += count * stride;
      if (total > GLTF_DECODED_MAX_BYTES) return "too_large";
    }
  }
  if ((Array.isArray(json.images) ? json.images.length : 0) > MAX_IMAGES) return "too_large";
  return null;
};

/** The names a glTF's buffers and images refer to (not data: URIs, not GLB-embedded). */
export const gltfReferences = (json) => [
  ...(Array.isArray(json?.buffers) ? json.buffers : []),
  ...(Array.isArray(json?.images) ? json.images : []),
].map((x) => (typeof x?.uri === "string" ? x.uri : "")).filter((u) => u && !u.startsWith("data:"));

/** The images a glTF embeds in its GLB binary chunk (bufferView slices; gltfImageBytes covers every source). */
export const gltfEmbeddedImages = (json, bin) => (Array.isArray(json?.images) ? json.images : [])
  .filter((im) => Number.isInteger(im?.bufferView) && bin)
  .map((im) => {
    const v = json.bufferViews?.[im.bufferView];
    const off = Number(v?.byteOffset ?? 0);
    const len = Number(v?.byteLength ?? 0);
    return off >= 0 && len > 0 && off + len <= bin.byteLength ? bin.subarray(off, off + len) : null;
  })
  .filter(Boolean);

/**
 * The texture files an MTL names, read as three's MTLLoader reads them (its
 * keys; the options -bm, -mm, -s, -o taken out; the rest, spaces included,
 * is the name), so what is fetched is exactly what the loader asks for.
 */
const MTL_MAP_KEYS = new Set(["map_kd", "map_ks", "map_ke", "norm", "map_bump", "bump", "disp", "map_d"]);
export const mtlReferences = (src) => String(src ?? "").split(/\r?\n/).map((raw) => {
  const line = raw.trim();
  const pos = line.indexOf(" ");
  if (pos < 0 || !MTL_MAP_KEYS.has(line.slice(0, pos).toLowerCase())) return "";
  const items = line.slice(pos + 1).trim().split(/\s+/);
  for (const [opt, n] of [["-bm", 2], ["-mm", 3], ["-s", 4], ["-o", 4]]) {
    const at = items.indexOf(opt);
    if (at >= 0) items.splice(at, n);
  }
  return items.join(" ").trim();
}).filter(Boolean);

/** A PNG's or JPEG's width and height from its header, or null when it is neither (other kinds are not checked). */
export const imageSize = (bytes) => {
  const b = bytes;
  if (b.length >= 24 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
    const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
    return { w: dv.getUint32(16), h: dv.getUint32(20) };
  }
  if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8) {
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) { i++; continue; }
      const m = b[i + 1];
      if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) return { h: (b[i + 5] << 8) | b[i + 6], w: (b[i + 7] << 8) | b[i + 8] };
      i += 2 + ((b[i + 2] << 8) | b[i + 3]);
    }
  }
  return null;
};

/**
 * Why an image may not be decoded in a look, or null. Only PNG and JPEG are
 * opened (their size is read from the header); any other kind (WebP, KTX2,
 * unknown) is refused, as an image too large for the look's pixel budget
 * (`pixels`: {left}, shared by all of a look's images) is.
 */
export const imageProblem = (bytes, pixels = null) => {
  const s = imageSize(bytes);
  if (!s) return "unsupported_image";
  if (s.w > MAX_IMAGE_SIDE || s.h > MAX_IMAGE_SIDE || s.w * s.h > MAX_IMAGE_PIXELS) return "too_large";
  if (pixels) {
    if (s.w * s.h > pixels.left) return "too_large";
    pixels.left -= s.w * s.h;
  }
  return null;
};

/** A data: URI's bytes (base64 or percent-encoded), or null. */
export const dataUriBytes = (uri) => {
  const m = /^data:([^,]*),(.*)$/s.exec(String(uri ?? ""));
  if (!m) return null;
  try {
    if (/;base64$/i.test(m[1])) return Uint8Array.from(atob(m[2]), (c) => c.charCodeAt(0));
    return new TextEncoder().encode(decodeURIComponent(m[2]));
  } catch {
    return null;
  }
};

/**
 * Every image a glTF names, as bytes, before anything is decoded: data: URIs,
 * companion files (`fileBytes`: name → bytes, already fetched) and buffer-view
 * slices of any buffer (the GLB's own chunk, or a companion .bin). An image
 * whose bytes cannot be found is null (the caller refuses it).
 */
export const gltfImageBytes = (json, bin, fileBytes, nameOf) => {
  const bufferBytes = (Array.isArray(json?.buffers) ? json.buffers : []).map((b, i) => {
    if (typeof b?.uri !== "string") return i === 0 ? bin : null;
    if (b.uri.startsWith("data:")) return dataUriBytes(b.uri);
    return fileBytes.get(nameOf(b.uri)) ?? null;
  });
  return (Array.isArray(json?.images) ? json.images : []).map((im) => {
    if (typeof im?.uri === "string") return im.uri.startsWith("data:") ? dataUriBytes(im.uri) : fileBytes.get(nameOf(im.uri)) ?? null;
    const v = Number.isInteger(im?.bufferView) ? json.bufferViews?.[im.bufferView] : null;
    const buf = v && Number.isInteger(v.buffer) ? bufferBytes[v.buffer] : null;
    const off = Number(v?.byteOffset ?? 0);
    const len = Number(v?.byteLength ?? 0);
    return buf && off >= 0 && len > 0 && off + len <= buf.byteLength ? buf.subarray(off, off + len) : null;
  });
};

/**
 * Why a PLY may not be built here, from its header alone, or null: every
 * element count a plain bounded integer, a vertex element with x, y and z,
 * and the counts possible for the file's size (a binary row has a fixed
 * size; an ASCII row takes at least two bytes), so no count can make the
 * loader run past its input or forever.
 */
const PLY_MAX_ELEMENTS = 20_000_000;
/** The most values a PLY or OBJ is decoded into (the loaders hold each as a JavaScript number first). */
const MESH_MAX_VALUES = 30_000_000;
const PLY_INT_TYPES = { char: "i1", uchar: "u1", int8: "i1", uint8: "u1", short: "i2", ushort: "u2", int16: "i2", uint16: "u2", int: "i4", uint: "u4", int32: "i4", uint32: "u4" };
const PLY_TYPE_BYTES = { char: 1, uchar: 1, int8: 1, uint8: 1, short: 2, ushort: 2, int16: 2, uint16: 2, int: 4, uint: 4, int32: 4, uint32: 4, float: 4, float32: 4, double: 8, float64: 8 };
/** Where `end_header` followed by a line break occurs in the bytes (every place, up to `max`). */
const headerEnds = (bytes, max = 2) => {
  const pat = [101, 110, 100, 95, 104, 101, 97, 100, 101, 114];
  const out = [];
  for (let i = bytes.indexOf(101); i >= 0 && i + pat.length < bytes.length; i = bytes.indexOf(101, i + 1)) {
    let ok = true;
    for (let k = 1; k < pat.length && ok; k++) ok = bytes[i + k] === pat[k];
    // Followed by any whitespace: the ASCII reader finds its body at the first `end_header\s+` anywhere.
    if (ok && [9, 10, 13, 32].includes(bytes[i + pat.length])) {
      out.push(i);
      if (out.length >= max) break;
    }
  }
  return out;
};
export const plyProblem = (bytes) => {
  const head = new TextDecoder("latin1").decode(bytes.subarray(0, Math.min(bytes.length, 64 * 1024)));
  if (!head.startsWith("ply")) return "unreadable";
  // Where the loader's header ends: a binary PLY at its first line that is exactly end_header (untrimmed);
  // an ASCII one at the last end_header and a line break in the whole file, so there must be only one
  // (and a line that is end_header once trimmed).
  const rawLines = head.split(/\r\n|\r|\n/);
  // Header lines are read as the loader reads them: words split on any whitespace.
  const fmtLines = rawLines.map((l) => l.trim().split(/\s+/)).filter((w) => w[0] === "format");
  if (fmtLines.length !== 1) return "unreadable";
  const fmt = fmtLines[0][1];
  const endLine = fmt === "ascii" ? rawLines.findIndex((l) => l.trim() === "end_header") : rawLines.findIndex((l) => l === "end_header");
  if (endLine < 0) return "unreadable";
  // The whole header, through the loader's own terminator line and its break: plain ASCII, its line breaks
  // all of one kind (the binary reader skips one more byte after the header when the first line ends in
  // CRLF), so it is read at the same offset as here.
  let headerEnd = -1;
  {
    const re = /([^\r\n]*)(\r\n|\r|\n)/g;
    let m;
    let n = 0;
    while ((m = re.exec(head)) !== null) {
      if (n === endLine) { headerEnd = m.index + m[0].length; break; }
      n++;
    }
  }
  if (headerEnd < 0) return "unreadable";
  const header = head.slice(0, headerEnd);
  const crlf = head.startsWith("ply\r\n");
  if (/[^\t\r\n\x20-\x7e]/.test(header)) return "unreadable";
  if (crlf ? /\r(?!\n)|(?<!\r)\n/.test(header) : /\r/.test(header)) return "unreadable";
  if (fmt === "ascii" && bytes.length > PLY_ASCII_MAX_BYTES) return "too_large";
  // An ASCII body in plain ASCII (the loader decodes UTF-8, whose spaces a byte scan would not see).
  if (fmt === "ascii") {
    for (let i = headerEnd; i < bytes.length; i++) {
      const b = bytes[i];
      if (!(b === 9 || b === 10 || b === 13 || (b >= 32 && b <= 126))) return "unreadable";
    }
  }
  const end = rawLines.slice(0, endLine).reduce((n, l) => n + l.length + 1, 0);
  const lines = rawLines.slice(0, endLine).map((l) => l.trim()).filter(Boolean);
  // One format line (the loader reads the last one there is) and each element once.
  const formats = lines.filter((l) => l.split(/\s+/)[0] === "format");
  if (formats.length !== 1) return "unreadable";
  const format = formats[0].split(/\s+/)[1];
  if (!["ascii", "binary_little_endian", "binary_big_endian"].includes(format)) return "unreadable";
  if (format === "ascii" && headerEnds(bytes).length !== 1) return "unreadable";
  const elements = [];
  for (const l of lines) {
    const w = l.split(/\s+/);
    if (w[0] === "element") {
      if (w.length !== 3 || !/^\d{1,9}$/.test(w[2])) return "unreadable";
      const count = Number(w[2]);
      if (count > PLY_MAX_ELEMENTS) return "too_large";
      elements.push({ name: w[1], count, props: [], row: 0, list: false });
    } else if (w[0] === "property") {
      const el = elements[elements.length - 1];
      if (!el) return "unreadable";
      if (w[1] === "list") {
        // A list's count and items are integers (the loader reads a float count as a float).
        if (!Object.hasOwn(PLY_INT_TYPES, w[2]) || !Object.hasOwn(PLY_INT_TYPES, w[3])) return "unsupported_model";
        el.list = true;
        el.row += PLY_TYPE_BYTES[w[2]] + PLY_TYPE_BYTES[w[3]];
        el.props.push(w[4]);
      } else {
        if (!Object.hasOwn(PLY_TYPE_BYTES, w[1])) return "unreadable";
        el.row += PLY_TYPE_BYTES[w[1]];
        el.props.push(w[2]);
      }
    }
  }
  if (elements.some((e) => !e.props.length)) return "unreadable";
  // Every vertex property a number held, every face up to two triangles' indices.
  const values = elements.reduce((n, e) => n + (e.name === "face" ? e.count * 6 : e.count * e.props.length), 0);
  if (values > MESH_MAX_VALUES) return "too_large";
  if (new Set(elements.map((e) => e.name)).size !== elements.length) return "unreadable";
  // A mesh or point cloud only: vertices (any properties) and faces (their index list only; per-face colours
  // or coordinates are expanded by the loader for every face declared).
  if (elements.some((e) => e.name !== "vertex" && e.name !== "face")) return "unsupported_model";
  const face = elements.find((e) => e.name === "face");
  if (face && (face.props.length !== 1 || !face.list || !["vertex_indices", "vertex_index"].includes(face.props[0]))) return "unsupported_model";
  if (vertexHasList(lines)) return "unsupported_model";
  const vertex = elements.find((e) => e.name === "vertex");
  if (!vertex || !["x", "y", "z"].every((p) => vertex.props.includes(p))) return "unreadable";
  const body = bytes.length - (end + "end_header".length);
  // The least each element's rows take: a binary row its fixed bytes (a list row at least its count and one
  // item); an ASCII row two bytes (one character and a line break).
  const least = elements.reduce((n, e) => n + e.count * (format === "ascii" ? 2 * e.props.length : Math.max(1, e.row)), 0);
  if (least > body) return "unreadable";
  // Every face a triangle or a quad, read row by row before the loader (a long list would be read into memory).
  if (face && face.count) {
    // Where the body starts: after the end_header line and its own line break, counted as the lines were split.
    const re = /([^\r\n]*)(\r\n|\r|\n)/g;
    let m;
    let n = 0;
    let bodyStart = -1;
    while ((m = re.exec(head)) !== null) {
      if (n === endLine) { bodyStart = m.index + m[0].length; break; }
      n++;
    }
    if (bodyStart < 0) return "unreadable";
    return plyFacesProblem(bytes, lines, format, elements, bodyStart);
  }
  return null;
};

/** Whether the vertex element has a list property (its rows would not have a fixed size). */
const vertexHasList = (lines) => {
  let inVertex = false;
  for (const l of lines) {
    const w = l.split(/\s+/);
    if (w[0] === "element") inVertex = w[1] === "vertex";
    else if (inVertex && w[0] === "property" && w[1] === "list") return true;
  }
  return false;
};

/** The face rows of a PLY whose elements are vertex then face (or face then vertex): every count 3 or 4. */
const plyFacesProblem = (bytes, lines, format, elements, bodyStart) => {
  let at = bodyStart;
  const sizes = { char: 1, uchar: 1, int8: 1, uint8: 1, short: 2, ushort: 2, int16: 2, uint16: 2, int: 4, uint: 4, int32: 4, uint32: 4, float: 4, float32: 4, double: 8, float64: 8 };
  const faceLine = lines.find((l) => /^property\s+list\s/.test(l));
  const [, , countType, itemType] = faceLine.split(/\s+/);
  if (format === "ascii") {
    // Plain ASCII only (the loader decodes UTF-8, whose spaces this scan would not see); then one token
    // stream, as the loader reads it: each row's properties in order, a list as its count and items.
    const body = bytes.subarray(at);
    for (let i = 0; i < body.length; i++) {
      const b = body[i];
      if (!(b === 9 || b === 10 || b === 13 || (b >= 32 && b <= 126))) return "unreadable";
    }
    const text = new TextDecoder("latin1").decode(body);
    const re = /\S+/g;
    const next = () => {
      const m = re.exec(text);
      return m ? m[0] : null;
    };
    const propsOf = (name) => {
      const out = [];
      let inEl = false;
      for (const l of lines) {
        const w = l.split(/\s+/);
        if (w[0] === "element") inEl = w[1] === name;
        else if (inEl && w[0] === "property") out.push(w[1] === "list");
      }
      return out;
    };
    for (const e of elements) {
      const props = propsOf(e.name);
      for (let i = 0; i < e.count; i++) {
        for (const isList of props) {
          const t = next();
          if (t === null) return "unreadable";
          if (!isList) continue;
          if (e.name === "face" && t !== "3" && t !== "4") return "unsupported_model";
          const n = Number(t);
          if (!Number.isInteger(n) || n < 0 || n > 4) return "unsupported_model";
          for (let k = 0; k < n; k++) if (next() === null) return "unreadable";
        }
      }
    }
    return null;
  }
  const little = format === "binary_little_endian";
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // Read as the loader reads it: the declared size and signedness, in the file's byte order.
  const readCount = (o) => {
    switch (PLY_INT_TYPES[countType]) {
      case "i1": return dv.getInt8(o);
      case "u1": return dv.getUint8(o);
      case "i2": return dv.getInt16(o, little);
      case "u2": return dv.getUint16(o, little);
      case "i4": return dv.getInt32(o, little);
      case "u4": return dv.getUint32(o, little);
      default: return -1;
    }
  };
  for (const e of elements) {
    if (e.name !== "face") {
      at += e.count * e.row;
      continue;
    }
    for (let i = 0; i < e.count; i++) {
      if (at + sizes[countType] > bytes.length) return "unreadable";
      const n = readCount(at);
      if (n !== 3 && n !== 4) return "unsupported_model";
      at += sizes[countType] + n * sizes[itemType];
    }
  }
  return at > bytes.length ? "unreadable" : null;
};

/** The most splats a look builds, and spherical-harmonic degree. */
export const SPLAT_MAX_POINTS = 10_000_000;
/**
 * Why an SPZ may not be built here, from its (gzipped) header, or null: the
 * first 16 decompressed bytes only (magic, version, the splat count, the
 * spherical-harmonic degree), the count and degree within the look's limit.
 */
export const spzProblem = async (bytes) => {
  if (typeof DecompressionStream !== "function") return "unreadable";
  try {
    // The whole payload is decompressed here once, counted and dropped as it streams: it may be no larger
    // than its header says (16 bytes and, per splat, its position, alpha, colour, scale, rotation and SH).
    const reader = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip")).getReader();
    const head = new Uint8Array(16);
    let got = 0;
    let cap = Infinity;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (got < 16) head.set(value.subarray(0, 16 - got), got);
      got += value.length;
      if (cap === Infinity && got >= 16) {
        const dv = new DataView(head.buffer);
        if (dv.getUint32(0, true) !== 0x5053474e) { reader.cancel().catch(() => {}); return "unreadable"; }
        const version = dv.getUint32(4, true);
        const points = dv.getUint32(8, true);
        const sh = head[12];
        if (version < 1 || version > 3 || sh > 3) { reader.cancel().catch(() => {}); return "unreadable"; }
        if (points > SPLAT_MAX_POINTS) { reader.cancel().catch(() => {}); return "too_large"; }
        const perPoint = 9 + 1 + 3 + 3 + (version >= 3 ? 4 : 3) + [0, 9, 24, 45][sh];
        cap = 16 + points * perPoint + 64 * 1024;
      }
      if (got > cap) { reader.cancel().catch(() => {}); return "too_large"; }
    }
    return got >= 16 ? null : "unreadable";
  } catch {
    return "unreadable";
  }
};

/**
 * A splat file's real format, from its first bytes (Spark decides from the
 * bytes, not the name): "spz" (gzip), "ply", "zip" (SOG or another bundle,
 * not opened here), or "raw" (.splat / .ksplat, no magic).
 */
export const splatFormatOf = (bytes) => {
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) return "spz";
  if (bytes[0] === 0x70 && bytes[1] === 0x6c && bytes[2] === 0x79) return "ply";
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) return "zip";
  return "raw";
};

/**
 * A glTF's JSON without what the review never shows and the loader would
 * copy or build anyway: `extras` at any depth, names past 64 characters,
 * and its cameras (the viewer has its own).
 */
const withoutExtras = (o, depth = 0) => {
  if (!o || typeof o !== "object" || depth > 64) return o;
  if (Array.isArray(o)) return o.map((x) => withoutExtras(x, depth + 1));
  const out = {};
  for (const [k, v] of Object.entries(o)) {
    if (k === "extras" || k === "camera" || (depth === 0 && k === "cameras")) continue;
    out[k] = k === "name" && typeof v === "string" ? v.slice(0, 64) : withoutExtras(v, depth + 1);
  }
  return out;
};

/**
 * What the loader is given: the checked JSON, re-serialized without extras
 * (a .gltf as text; a GLB rebuilt as its new JSON chunk and the same BIN
 * chunk), so it reads exactly what was checked.
 */
export const gltfForLoader = (parts, name) => {
  const text = JSON.stringify(withoutExtras(parts.json));
  if (/\.gltf$/i.test(String(name))) return text;
  const enc = new TextEncoder().encode(text);
  const jsonLen = Math.ceil(enc.length / 4) * 4;
  const binLen = parts.bin ? Math.ceil(parts.bin.length / 4) * 4 : 0;
  const total = 12 + 8 + jsonLen + (parts.bin ? 8 + binLen : 0);
  const out = new Uint8Array(total);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, 0x46546c67, true); dv.setUint32(4, 2, true); dv.setUint32(8, total, true);
  dv.setUint32(12, jsonLen, true); dv.setUint32(16, 0x4e4f534a, true);
  out.fill(0x20, 20, 20 + jsonLen);
  out.set(enc, 20);
  if (parts.bin) {
    const at = 20 + jsonLen;
    dv.setUint32(at, binLen, true); dv.setUint32(at + 4, 0x004e4942, true);
    out.set(parts.bin, at + 8);
  }
  return out.buffer;
};

/**
 * The GPU uploads a glTF's textures make: one per distinct image and sampler
 * pair (the renderer keeps a copy per sampler setting). Returns, per image
 * index, how many copies it is uploaded as (0 for an image no texture uses).
 */
export const gltfTextureCopies = (json) => {
  const images = Array.isArray(json?.images) ? json.images : [];
  const copies = images.map(() => new Set());
  for (const t of Array.isArray(json?.textures) ? json.textures : []) {
    if (Number.isInteger(t?.source) && copies[t.source]) copies[t.source].add(Number.isInteger(t?.sampler) ? t.sampler : -1);
  }
  return copies.map((c) => c.size);
};

/** The most vertices an OBJ's faces, lines and points may name in all (each becomes vertex data). */
const OBJ_MAX_FACE_VERTICES = 30_000_000;
/** The most vertex records (v, vt, vn), and objects, groups and material switches (each a mesh and a draw). */
const OBJ_MAX_VERTEX_LINES = 20_000_000;
const OBJ_MAX_GROUPS = 1_000;
/** The most materials an MTL may declare (each one built). */
export const MTL_MAX_MATERIALS = 1_000;
/**
 * Why an OBJ may not be built here, from its text read as three's OBJLoader
 * reads it (CRLF to LF, backslash continuations joined, each line's leading
 * whitespace trimmed, dispatched by its first character), or null: faces,
 * lines and points of at most 4 vertices (a longer face is fanned into
 * triangles, copying vertices), and the vertices, records and groups each
 * within a cap.
 */
export const objProblem = (src) => {
  let text = String(src ?? "");
  if (text.indexOf("\r\n") !== -1) text = text.replace(/\r\n/g, "\n");
  if (text.indexOf("\\\n") !== -1) text = text.replace(/\\\n/g, "");
  let refs = 0;
  let verts = 0;
  let groups = 0;
  let values = 0;
  let from = 0;
  while (from <= text.length) {
    let to = text.indexOf("\n", from);
    if (to === -1) to = text.length;
    const line = text.slice(from, to).trimStart();
    from = to + 1;
    const c = line.charAt(0);
    if (c === "" || c === "#") continue;
    if (c === "v") {
      if (++verts > OBJ_MAX_VERTEX_LINES) return "too_large";
      // A position and a colour (a placeholder when it has none) per record.
      values += 6;
      if (values > MESH_MAX_VALUES) return "too_large";
    } else if (c === "f" || c === "l" || c === "p") {
      const rest = line.slice(1).trim();
      const n = (c === "f" ? rest.split(/\s+/) : rest.split(" ")).filter((x) => x.length > 0).length;
      if (n > 4 || (c !== "f" && rest.split(" ").length > 4)) return "unsupported_model";
      refs += n;
      if (refs > OBJ_MAX_FACE_VERTICES) return "too_large";
      // Each face vertex becomes a position, a normal, a colour and a UV (a quad two triangles: six vertices).
      values += (c === "f" && n === 4 ? 6 : n) * 11;
      if (values > MESH_MAX_VALUES) return "too_large";
    } else if (/^[og]\s*(.+)?/.test(line) || /^usemtl /.test(line)) {
      if (++groups > OBJ_MAX_GROUPS) return "too_large";
    }
  }
  return null;
};

/** How many materials an MTL declares, read as MTLLoader reads its keys (trimmed, lowercased). */
export const mtlMaterialCount = (src) => String(src ?? "").split(/\r?\n/)
  .filter((l) => /^newmtl(\s|$)/i.test(l.trim())).length;
